import lodash from 'lodash'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import { GeckoEditableComponent } from 'types/assetModel/components/geckoEditableComponent'
import {
  GeckoLegacyBoilerRepairRegion,
  GeckoLegacyBoilerRepairRegionJSON,
} from 'types/assetModel/features/legacyBoiler/geckoLegacyBoilerRepairRegion'
import {
  GeckoLegacyBoilerWall,
  GeckoLegacyBoilerWallJSON,
  validateTubeMeasurement,
} from 'types/assetModel/features/legacyBoiler/geckoLegacyBoilerWall'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { parseDate, parseNumber, parseString } from 'utils/parsingHelpers'
import {
  optionalDateStringWithRegex,
  optionalNumberSchema,
  optionalStringSchema,
  requiredIntSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export type LegacyBoilerWallsJSON = {
  type: GeckoComponentModelType.LegacyBoilerWalls
  name: string
  height: number
  tube_count: number
  installation_date: string | undefined
  material: string | undefined
  component_bounds: {
    tube_start: number
    tube_end: number
    y_start: number
    y_end: number
  }
  tubes: {
    tube_diameter: number | undefined
    tube_spacing: number | undefined
  }
  scene_config: Record<string, unknown>
  features: Record<
    string,
    GeckoLegacyBoilerWallJSON | GeckoLegacyBoilerRepairRegionJSON
  >
  compass_orientation?: number
}

export const LegacyBoilerWallsAvailableFeatures: GeckoFeatureModelType[] = [
  GeckoFeatureModelType.LegacyBoilerWall,
  GeckoFeatureModelType.LegacyBoilerRepairRegion,
]

export const NewLegacyBoilerWallsJSON = {
  type: GeckoComponentModelType.LegacyBoilerWalls,
  name: 'New Legacy Boiler Walls',
  height: 0,
  installation_date: undefined,
  material: undefined,
  tube_count: 0,
  component_bounds: {
    tube_start: 0,
    tube_end: 0,
    y_start: 0,
    y_end: 0,
  },
  tubes: {},
  scene_config: {},
  features: {},
} as LegacyBoilerWallsJSON

export const TUBESPACING_DEFAULT = GeckoMeasurement.CreateFromValue(
  0.0508,
  GeckoMeasurementUnit.Meters
)

export class GeckoLegacyBoilerWalls extends GeckoEditableComponent {
  type: GeckoComponentModelType.LegacyBoilerWalls
  tubeCount: GeckoMeasurement
  height: GeckoMeasurement
  installationDate: Date | undefined
  material: string | undefined
  tubeDiameter: GeckoMeasurement | undefined
  tubeSpacing: GeckoMeasurement | undefined
  sceneConfig = undefined
  componentBounds: GeckoComponentRect
  walls: Map<string, GeckoLegacyBoilerWall>
  repairRegions: Map<string, GeckoLegacyBoilerRepairRegion>

  private constructor(
    id: string,
    name: string,
    json: Record<string, unknown>,
    tubeCount: GeckoMeasurement,
    height: GeckoMeasurement,
    installationDate: Date | undefined,
    material: string | undefined,
    tubeSpacing: GeckoMeasurement | undefined,
    tubeDiameter: GeckoMeasurement | undefined,
    componentBounds: GeckoComponentRect,
    walls: Map<string, GeckoLegacyBoilerWall>,
    repairRegions: Map<string, GeckoLegacyBoilerRepairRegion>
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.LegacyBoilerWalls
    validateTubeMeasurement(tubeCount, id, 'GeckoLegacyBoilerWalls.tube_count')
    this.tubeCount = tubeCount
    this.height = height
    this.installationDate = installationDate
    this.material = material
    this.tubeDiameter = tubeDiameter
    this.tubeSpacing = tubeSpacing
    validateTubeMeasurement(
      componentBounds.x.start,
      id,
      'GeckoLegacyBoilerWalls.componentBounds.x_start'
    )
    validateTubeMeasurement(
      componentBounds.x.end,
      id,
      'GeckoLegacyBoilerWalls.componentBounds.x_end'
    )
    this.componentBounds = GeckoComponentRect.CreateFromXYs(
      componentBounds.componentID,
      componentBounds.x.start.num - 0.5,
      componentBounds.y.start.num,
      componentBounds.x.end.num + 0.5,
      componentBounds.y.end.num,
      componentBounds.x.unit,
      componentBounds.y.unit
    )
    this.walls = walls
    this.repairRegions = repairRegions
  }

  public get wallsArray() {
    return Array.from(this.walls.values())
  }

  public get repairRegionsArray() {
    return Array.from(this.repairRegions.values())
  }

  public static CreateFromJSON(id: string, json: LegacyBoilerWallsJSON) {
    const name = json.name as string

    const waterwallsWidth = GeckoMeasurement.CreateFromValue(
      json.tube_count as number,
      GeckoMeasurementUnit.Tubes
    )
    const waterwallsHeight = GeckoMeasurement.CreateFromValue(
      json.height as number,
      GeckoMeasurementUnit.Meters
    )

    const tubes = json.tubes as Record<string, number>

    const tubeSpacing =
      tubes.tube_spacing === undefined
        ? undefined
        : GeckoMeasurement.CreateFromValue(
            tubes.tube_spacing,
            GeckoMeasurementUnit.Meters
          )

    const tubeDiameter =
      tubes.tube_diameter === undefined
        ? undefined
        : GeckoMeasurement.CreateFromValue(
            tubes.tube_diameter,
            GeckoMeasurementUnit.Meters
          )

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.tube_start,
      componentBoundsData.y_start,
      componentBoundsData.tube_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Tubes,
      GeckoMeasurementUnit.Meters
    )

    const featureData = json.features as Record<
      string,
      GeckoLegacyBoilerWallJSON | GeckoLegacyBoilerRepairRegionJSON
    >

    const childWallJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.LegacyBoilerWall
    })

    const walls = GeckoLegacyBoilerWall.CreateMapFromJSON(
      childWallJSON as Record<string, GeckoLegacyBoilerWallJSON>,
      componentBounds
    )

    const repairRegionJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.LegacyBoilerRepairRegion
    })

    const repairRegions = GeckoLegacyBoilerRepairRegion.CreateMapFromJSON(
      id,
      repairRegionJSON as Record<string, GeckoLegacyBoilerRepairRegionJSON>
    )

    const installationDate = json.installation_date
      ? parseDate(json.installation_date)
      : undefined

    const material = json.material ?? undefined

    return new GeckoLegacyBoilerWalls(
      id,
      name,
      json,
      waterwallsWidth,
      waterwallsHeight,
      installationDate,
      material,
      tubeSpacing,
      tubeDiameter,
      componentBounds,
      walls,
      repairRegions
    )
  }

  public asJSON(): LegacyBoilerWallsJSON {
    return generateLegacyBoilerWalls(
      this.name,
      this.height,
      this.installationDate,
      this.material,
      this.tubeCount,
      this.tubeSpacing,
      this.tubeDiameter,
      this.componentBounds.y.start,
      this.json as LegacyBoilerWallsJSON
    )
  }
}

const generateLegacyBoilerWalls = (
  name: string,
  height: GeckoMeasurement,
  installationDate: Date | undefined,
  material: string | undefined,
  tubeCount: GeckoMeasurement,
  tubeSpacing: GeckoMeasurement | undefined,
  tubeDiameter: GeckoMeasurement | undefined,
  yStart: GeckoMeasurement,
  otherLegacyBoilerWallsJSON: LegacyBoilerWallsJSON = NewLegacyBoilerWallsJSON
): LegacyBoilerWallsJSON => {
  const convertedYStart = yStart
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num
  const boilerWallsHeight = height
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const boilerTubeSpacing =
    tubeSpacing !== undefined
      ? tubeSpacing
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined
  const boilerTubeDiameter =
    tubeDiameter !== undefined
      ? tubeDiameter
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const installationDateParsed = installationDate
    ? installationDate.toISOString().split('T')[0]
    : undefined

  return {
    name: name,
    type: GeckoComponentModelType.LegacyBoilerWalls,
    height: boilerWallsHeight,
    installation_date: installationDateParsed,
    material: material,
    tube_count: tubeCount.num,
    tubes: {
      tube_diameter: boilerTubeDiameter,
      tube_spacing: boilerTubeSpacing,
    },
    features: otherLegacyBoilerWallsJSON.features,
    scene_config: otherLegacyBoilerWallsJSON.scene_config,
    component_bounds: {
      tube_start: 1,
      y_start: convertedYStart,
      tube_end: tubeCount.num,
      y_end: convertedYStart + boilerWallsHeight,
    },
  }
}

export const LegacyBoilerWallsAssetModelFormFields = (
  legacyBoilerWallsJSON: LegacyBoilerWallsJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Legacy Boiler Walls Name',
      value: legacyBoilerWallsJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'height',
      label: 'Height',
      value: legacyBoilerWallsJSON.height,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'installation_date',
      label: 'Installation Date',
      value: legacyBoilerWallsJSON.installation_date,
      unit_magnitude: null,
      validationRule: optionalDateStringWithRegex,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Date,
    },
    {
      dataKey: 'material',
      label: 'Material',
      value: legacyBoilerWallsJSON.material,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'y_start',
      label: 'Y Start',
      value: legacyBoilerWallsJSON.component_bounds.y_start,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'tube_count',
      label: 'Tube Count',
      value: legacyBoilerWallsJSON.tube_count,
      unit_magnitude: GeckoMeasurementUnit.Tubes,
      validationRule: requiredIntSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'tube_spacing',
      label: 'Tube Spacing',
      value: legacyBoilerWallsJSON.tubes.tube_spacing,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'tube_diameter',
      label: 'Tube Diameter',
      value: legacyBoilerWallsJSON.tubes.tube_diameter,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const LegacyBoilerWallsJSONFromFormFields = (
  component: LegacyBoilerWallsJSON,
  formFields: FormField[]
) => {
  const parsedTubeSpacing = parseNumber(
    getFormFieldByDataKey(formFields, 'tube_spacing')?.value
  )
  const parsedTubeDiameter = parseNumber(
    getFormFieldByDataKey(formFields, 'tube_diameter')?.value
  )
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  return generateLegacyBoilerWalls(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'height')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    installationDate?.value !== undefined
      ? new Date(installationDate?.value as string)
      : undefined,
    parseString(getFormFieldByDataKey(formFields, 'material')?.value),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'tube_count')?.value as number,
      GeckoMeasurementUnit.Tubes
    ),
    parsedTubeSpacing !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedTubeSpacing,
          GeckoMeasurementUnit.Tubes
        )
      : undefined,
    parsedTubeDiameter !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedTubeDiameter,
          GeckoMeasurementUnit.Tubes
        )
      : undefined,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y_start')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    component
  )
}
